
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, OnDestroy, ChangeDetectorRef, HostListener } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { MdlDialogReference, MdlSnackbarService } from "@angular-mdl/core";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { RootState, getOnlineStatus, getUserProfile } from "src/app/reducers";
import { takeUntil, take } from "rxjs/operators";
import { TFARepository } from "src/app/services/tfa.repository";
import { MailBroadcaster } from "src/app/common/providers/mail-broadcaster.service";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";

@Component({
  selector: "vp-tfa-settings",
  templateUrl: "./tfa-settings.html"
})
export class TFASettingsComponent implements OnDestroy {
  app = "vnclagoon";
  updateForm: FormGroup;
  otpCode: FormControl;
  qrCode: any;
  secret: any;
  email: any;
  private isAlive$ = new Subject<boolean>();
  isAppOnline: boolean;
  isManualEnabled: boolean = false;
  isMobileScreen: boolean = false;
  currentTheme = localStorage.getItem("theme");
  constructor(
    private dialog: MdlDialogReference,
    private translate: TranslateService,
    private snackBarService: MdlSnackbarService,
    private changeDetectorRef: ChangeDetectorRef,
    private tfaRepo: TFARepository,
    private store: Store<RootState>,
    private broadcaster: MailBroadcaster,
    private breakpointObserver: BreakpointObserver
  ) {
    this.setupStore();
    this.isMobileScreen = this.breakpointObserver.isMatched("(max-width: 767px)");
    this.breakpointObserver
      .observe(["(max-width: 767px)"])
      .pipe(takeUntil(this.isAlive$))
      .subscribe((state: BreakpointState) => {
        if (state.matches) {
          this.isMobileScreen = true;
        } else {
          this.isMobileScreen = false;
        }
        this.changeDetectorRef.markForCheck();
      });
    this.broadcaster.on<any>("hideTfaSettings")
      .pipe(takeUntil(this.isAlive$))
      .subscribe(data => {
        this.dialog.hide();
      });

    this.otpCode = new FormControl("", [Validators.required]);
    const defaultGroup = {
      "otpCode": this.otpCode
    };
    this.updateForm = new FormGroup(defaultGroup);
    this.secret = this.createSecret();
    const profileUser = localStorage.getItem("profileUser");
    let currentUser = MailUtils.parseUserProfile(profileUser);
    this.email = currentUser.email;
    this.qrCode = this.getQRCodeGoogleUrl(this.app, this.email, this.secret);
    this.changeDetectorRef.markForCheck();
  }

  setupStore() {
    this.store.select(getOnlineStatus).pipe(takeUntil(this.isAlive$)).subscribe(v => this.isAppOnline = v);
  }

  ngOnDestroy() {
    this.isAlive$.next(false);
    this.isAlive$.complete();

    if (document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "none";
    }
  }

  getBase32LookupTable() {
    return [
        "A", "B", "C", "D", "E", "F", "G", "H", //  7
        "I", "J", "K", "L", "M", "N", "O", "P", // 15
        "Q", "R", "S", "T", "U", "V", "W", "X", // 23
        "Y", "Z", "2", "3", "4", "5", "6", "7", // 31
        "="  // padding char
    ];
  }

  createSecret(secretLength = 16) {
    let validChars = this.getBase32LookupTable();
    let secret = "";
    for (let i = 0; i < secretLength; i++) {
        secret += validChars[Math.floor(Math.random() * (validChars.length - 1 ))];
    }
    return secret;
  }

  getQRCodeGoogleUrl(app, name, secret) {
    let urlencoded = encodeURI("otpauth://totp/" + app + "(" + name + ")?secret=" + secret);
    return "https://chart.googleapis.com/chart?chs=200x200&chld=M|0&cht=qr&chl=" + urlencoded;
  }

  @HostListener("document:keydown.esc")
  onEsc(): void {
    this.dialog.hide();
  }

  submitForm() {
    if (!this.isAppOnline) {
      this.translate.get(["OFFLINE_MODE"])
        .pipe(takeUntil(this.isAlive$))
        .subscribe(res => {
          this.snackBarService.showToast(res.OFFLINE_MODE);
        });
      return;
    }
    if (this.otpCode && this.otpCode.value) {
      this.tfaRepo.enableTFA(this.secret).subscribe( res => {
        this.tfaRepo.verify2faOTP(this.otpCode.value).subscribe( res => {
          this.translate.get(["TFA_CONFIGURED_SUCCESS"])
          .pipe(take(1))
          .subscribe(res => {
            this.snackBarService.showToast(res.TFA_CONFIGURED_SUCCESS);
            this.dialog.hide(true);
          });
        }, err => {
          console.log("[TFASettingsComponet][submitForm] Enter wrong otp:", err);
          this.translate.get(["ENTER_WRONG_OTP"])
          .pipe(take(1))
          .subscribe(res => {
            this.snackBarService.showToast(res.ENTER_WRONG_OTP);
          });
          this.changeDetectorRef.markForCheck();
        });
      }, err => {
        console.log("[TFASettingsComponet][submitForm] error saving 2fa settings:", err);
        this.translate.get(["ERROR_SAVE_2FA_SETTING"])
          .pipe(take(1))
          .subscribe(res => {
            this.snackBarService.showToast(res.ERROR_SAVE_2FA_SETTING);
          });
      });
    }
  }
}
